import { useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useLang, STORAGE_KEY } from '../utils/i18n';
import { langFromPath, isSupportedLang, pathWithLang, DEFAULT_LANG } from '../utils/languages';

// Lazy: only rendered for invalid language prefixes (/nonsense, /xyz) — never
// reached by normal users, so keeping it out of the main bundle is safe.
const NotFound = lazy(() => import('../pages/NotFound'));
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Keeps the active language in step with the URL.
 *
 * The path is the source of truth: /es/about must render Spanish regardless of
 * what the visitor picked last time. LanguageProvider reads the path once at
 * mount, but client-side navigation changes the path without remounting, so
 * this bridges the two.
 *
 * Lives inside the Router because it needs useLocation; LanguageProvider sits
 * outside it and cannot.
 */
export default function LangSync() {
  const { pathname, search, hash } = useLocation();
  const { lang, adoptLang } = useLang();
  const navigate = useNavigate();

  const fromPath = langFromPath(pathname) ?? DEFAULT_LANG;
  const isEmbedPath = pathname === '/embed' || pathname.startsWith('/embed/');

  useClientLayoutEffect(() => {
    if (fromPath !== lang) adoptLang(fromPath);
  }, [fromPath, lang, adoptLang]);

  // Links shared before the move to path prefixes carry ?lang=xx. Rewrite them
  // to the canonical prefixed URL so there is exactly one address per language
  // — two URLs serving the same content is the duplicate-content problem this
  // whole scheme exists to avoid.
  useEffect(() => {
    if (isEmbedPath) return;
    const legacy = new URLSearchParams(search).get('lang');
    if (!isSupportedLang(legacy)) return;
    const params = new URLSearchParams(search);
    params.delete('lang');
    const qs = params.toString();
    // <!-- SEO: no untranslated Team Picker aliases; use the localized team tool. -->
    const targetPath = pathname.replace(/\/$/, '') === '/team-picker' && legacy !== DEFAULT_LANG
      ? '/random-team-generator/' : pathname;
    navigate(
      { pathname: pathWithLang(targetPath, legacy), search: qs ? `?${qs}` : '', hash },
      { replace: true },
    );
  }, [search, pathname, hash, navigate, isEmbedPath]);

  /**
   * Send a returning visitor to their own language.
   *
   * Moving to path prefixes broke this. The bare path is canonically English,
   * and the effect above forces the active language to match it — so someone
   * who had chosen Spanish landed on `/` and got English, with their stored
   * preference silently overridden on every visit.
   *
   * Redirecting to the prefixed URL honours the preference AND keeps the URL
   * truthful, rather than serving Spanish from a page whose own canonical and
   * hreflang tags say it is the English one.
   *
   * Only an explicit choice from the picker is stored (rememberLang persists,
   * adoptLang does not), so merely following a shared /fr/ link never arms
   * this. It cannot loop: after the redirect the path carries a prefix, and
   * the guard below stops.
   */
  useEffect(() => {
    if (isEmbedPath) return;
    // <!-- SEO: keep explicit visits to the English-only guide on its real URL. -->
    if (pathname.replace(/\/$/, '') === '/team-picker') return;
    if (langFromPath(pathname)) return;            // already prefixed
    if (new URLSearchParams(search).has('lang')) return; // legacy rewrite owns this pass
    let stored: string | null = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* blocked */ }
    if (!isSupportedLang(stored) || stored === DEFAULT_LANG) return;
    navigate({ pathname: pathWithLang(pathname, stored), search, hash }, { replace: true });
  }, [pathname, search, hash, navigate, isEmbedPath]);

  return null;
}

/**
 * Guards the /:lang branch of the route tree.
 *
 * `:lang` is a dynamic segment, so it matches any unknown first path segment
 * that did not match a static route — /nonsense would otherwise render the
 * homepage under a bogus language. Anything that is not one of the 30 prefixed
 * languages is redirected to the English equivalent, which either exists or
 * falls through to the real 404.
 */
export function LangGate({ lang, children }: { lang?: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  // `/en/...` is not part of the scheme — English is the bare path, and a second
  // URL serving identical English would be a duplicate of it.
  if (!isSupportedLang(lang) || lang === DEFAULT_LANG) {
    // Strip the bogus prefix to get the remaining path.
    const bare = pathname.replace(/^\/[^/]+/, '') || '/';
    if (bare === '/') {
      // Single-segment path like `/nonsense` — render the 404 page directly
      // rather than redirecting (which would loop through `:lang` again).
      return <Suspense fallback={null}><NotFound /></Suspense>;
    }
    // Multi-segment: `/nonsense/about` → `/about`
    return <Navigate to={bare} replace />;
  }
  return <>{children}</>;
}
